/**
 * Local alarm helper — schedules OS-native reminders on iOS + Android
 * via @capacitor/local-notifications. This uses iOS UNUserNotifi-
 * cationCenter and Android AlarmManager under the hood, so:
 *   • No server push infrastructure required.
 *   • No Apple review needed — local notifications don't touch APNs.
 *   • No FCM / google-services.json for Android.
 *
 * On a plain web browser (non-Capacitor), we gracefully no-op so the
 * PWA/site keeps working — the user just doesn't get the alarm.
 *
 * SOUND POLICY:
 *   • iOS: `sound: "default"` on each schedule → plays the system
 *     "notification.caf" alert tone. Passing `undefined` on iOS
 *     produces a silent banner (per @capacitor/local-notifications
 *     docs) — which is why we default it here.
 *   • Android 8+: sound is controlled by a NotificationChannel, not
 *     per-notification. We create a `calofay_reminders` channel with
 *     IMPORTANCE_HIGH + default sound + vibration on first use, then
 *     reference it via `channelId` on every schedule.
 */
import { Capacitor } from "@capacitor/core";

const isNative = () => {
  try { return typeof Capacitor !== "undefined" && Capacitor.isNativePlatform && Capacitor.isNativePlatform(); }
  catch { return false; }
};

// Convert a user-visible ID (task id / recurring template id) to the
// integer id Capacitor requires. Deterministic djb2 so the same ID
// always maps to the same schedule slot (idempotent reschedule).
function toIntId(str) {
  let h = 5381;
  const s = String(str || "unknown");
  for (let i = 0; i < s.length; i += 1) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
  return (h % 2147000000) + 1000; // avoid collision with Capacitor internals
}

// Fixed identifier for the reminders channel on Android — must match
// the value we pass to schedule() below and must NOT change between
// releases (Android caches channel settings the first time they're
// created and further changes require the user to reset them).
const REMINDER_CHANNEL_ID = "calofay_reminders";
let _channelReadyPromise = null;

// Bundled alarm ringtones live at:
//   • Android → `res/raw/alarm.mp3`  (~236 KB, 20 s of alternating beeps)
//   • iOS     → `App/App/alarm.caf`  (~1.7 MB, 20 s)
// They are NOT wired in yet because we haven't rebuilt the native
// bundles that would include them. Until a new .aab / iOS Archive
// ships with those assets bundled, we deliberately fall back to the
// OS-default notification tone so the alarm still SOUNDS — just with
// the shorter system "ding" instead of the custom 20-second pattern.
// When you're ready to enable the custom tone, flip these constants
// to the filenames above and ship a native release.
const ALARM_SOUND_ANDROID = null;   // e.g. "alarm"     → res/raw/alarm.mp3
const ALARM_SOUND_IOS     = "default"; // e.g. "alarm.caf" → iOS bundle

async function ensureChannel() {
  // Only Android >= 8 needs channels; on iOS this call is a no-op
  // inside the plugin, but we still guard with the platform check
  // to skip the dynamic import on iOS.
  if (!isNative() || Capacitor.getPlatform?.() !== "android") return;
  if (_channelReadyPromise) return _channelReadyPromise;
  _channelReadyPromise = (async () => {
    try {
      const { LocalNotifications } = await import("@capacitor/local-notifications");
      const channelConfig = {
        id: REMINDER_CHANNEL_ID,
        name: "Reminders",
        description: "Task reminders and daily alarms from Calofay",
        // IMPORTANCE_HIGH (4) — heads-up banner + sound + vibration.
        importance: 4,
        visibility: 1,
        vibration: true,
        lights: true,
        lightColor: "#F97316",
      };
      // Only pin the sound to a bundled file when we have one. Passing
      // `undefined` (via omit) tells Android to use the system default
      // notification tone, which is the behaviour we want until the
      // native bundle ships with alarm.mp3.
      if (ALARM_SOUND_ANDROID) channelConfig.sound = ALARM_SOUND_ANDROID;
      await LocalNotifications.createChannel(channelConfig);
    } catch (_) { /* older Android versions don't need channels — ignore */ }
  })();
  return _channelReadyPromise;
}

async function ensurePermission() {
  if (!isNative()) return false;
  const { LocalNotifications } = await import("@capacitor/local-notifications");
  const s = await LocalNotifications.checkPermissions();
  if (s.display === "granted") { await ensureChannel(); return true; }
  const r = await LocalNotifications.requestPermissions();
  if (r.display === "granted") { await ensureChannel(); return true; }
  return false;
}

/** Schedule a one-shot notification at a specific ISO datetime. */
export async function scheduleOneShot({ id, title, body, atISO, url }) {
  if (!isNative()) return { ok: false, reason: "web" };
  const ok = await ensurePermission();
  if (!ok) return { ok: false, reason: "denied" };
  const { LocalNotifications } = await import("@capacitor/local-notifications");
  const at = new Date(atISO);
  if (Number.isNaN(at.getTime()) || at.getTime() < Date.now() + 60_000) {
    return { ok: false, reason: "past" };
  }
  await LocalNotifications.schedule({
    notifications: [{
      id: toIntId(id),
      title, body,
      // Per-notification sound. iOS: "default" plays the system alert
      // tone. Android 8+ ignores this and uses the channel's sound
      // instead (see `createChannel` above) — on Android 7 the OS
      // falls back to this string.
      sound: ALARM_SOUND_IOS,
      channelId: REMINDER_CHANNEL_ID,
      schedule: { at, allowWhileIdle: true },
      extra: { url: url || "/app/calendar" },
    }],
  });
  return { ok: true };
}

/** Schedule a repeating notification (daily / weekly / monthly) at HH:MM. */
export async function scheduleRepeating({ id, title, body, hhmm, pattern, weekday, dayOfMonth, url }) {
  if (!isNative()) return { ok: false, reason: "web" };
  const ok = await ensurePermission();
  if (!ok) return { ok: false, reason: "denied" };
  const { LocalNotifications } = await import("@capacitor/local-notifications");
  const [h, m] = (hhmm || "09:00").split(":").map(Number);
  // Capacitor's `schedule.on` uses local time components; setting
  // `repeats: true` makes iOS/Android re-fire on the next matching
  // combination automatically (day for daily, weekday for weekly,
  // day-of-month for monthly).
  const on = { hour: h, minute: m };
  if (pattern === "weekly" && typeof weekday === "number") on.weekday = weekday + 1; // Capacitor: Sun=1..Sat=7
  if (pattern === "monthly" && typeof dayOfMonth === "number") on.day = dayOfMonth;
  await LocalNotifications.schedule({
    notifications: [{
      id: toIntId(id),
      title, body,
      sound: ALARM_SOUND_IOS,
      channelId: REMINDER_CHANNEL_ID,
      schedule: { on, repeats: true, allowWhileIdle: true },
      extra: { url: url || "/app/calendar" },
    }],
  });
  return { ok: true };
}

export async function cancelAlarm(id) {
  if (!isNative()) return;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    await LocalNotifications.cancel({ notifications: [{ id: toIntId(id) }] });
  } catch { /* ignore */ }
}

/**
 * Returns the OS permission state for LocalNotifications:
 *   "granted" | "denied" | "prompt" | "prompt-with-rationale" | "web"
 * "web" is returned when we're not running inside a Capacitor shell —
 * callers should treat that as a soft "unavailable" and show the
 * "Works in the app" hint instead of a permission prompt.
 */
export async function checkAlarmPermission() {
  if (!isNative()) return "web";
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const s = await LocalNotifications.checkPermissions();
    return s.display;
  } catch { return "denied"; }
}

/**
 * Fires the OS permission dialog directly. Returns true if granted.
 * Used by `PushPromptCard.enable()` after the server-push permission
 * flow so both Capacitor plugins (PushNotifications + LocalNotifi-
 * cations) land in the granted state — they share the same OS slot
 * on iOS/Android but the SDKs cache state per-plugin.
 */
export async function ensureAlarmPermission() {
  if (!isNative()) return false;
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const s = await LocalNotifications.checkPermissions();
    if (s.display === "granted") { await ensureChannel(); return true; }
    const r = await LocalNotifications.requestPermissions();
    if (r.display === "granted") { await ensureChannel(); return true; }
    return false;
  } catch { return false; }
}

export const alarmsSupported = () => isNative();
